<template>
  <div v-if="isLoggedIn">
    <h4>YOU CANNOT ACCESS THIS PAGE WHILE LOGGED IN. PLEASE LOG OUT AND RE-OPEN THE LINK TO ACCESS.</h4>
  </div>
  <div v-else class="content">
    <div class="reset-wrapper">
      <div class="reset">
        <PublicBrandHeader />
        <h3>Provider Account Setup</h3>
        <p class="invite-notice">{{ inviteNotice }}</p>

        <div class="row mb-2 gx-2">
          <div class="col-md-6 col-12 form-group form-floating mb-2 mb-lg-0">
            <input type="text" class="form-control" :value="accountTypeLabel" disabled />
            <label class="profile_label">Account Type:</label>
          </div>
          <div class="col-md-6 col-12 form-group form-floating mb-2 mb-lg-0">
            <input type="text" class="form-control" :value="accntnum" disabled />
            <label class="profile_label">Organization Account #:</label>
          </div>
        </div>

        <p class="invite-notice">
          Provider accounts are no longer created here. Your username and password are
          chosen at the pacmny sign-in service, which this application never handles,
          and the rest of your provider record — NPI, provider type, address and which
          patients you are on the care team for — is set up by the organization that
          invited you.
        </p>
        <p class="invite-notice">
          Continue to sign in below. If you cannot find your invitation, request a
          new link. If you have no invitation yet, please ask your organization to
          send one.
        </p>
        <p class="invite-notice invite-expired">
          Invitations expire 30 days after they are sent. If the sign-in service
          tells you "{{ inviteExpired }}", only your organization can send you a
          new invitation; requesting a new link will not.
        </p>

        <div class="accountform-btn">
          <button
            type="button"
            class="btn btn-primary"
            id="sbmtproviderbtn"
            :disabled="redirecting"
            @click="continueToSignIn"
          >
            {{ redirecting ? 'Taking you there...' : 'Continue to sign in' }}
          </button>
          <a :href="forgotPasswordUrl" class="btn btn-secondary">Request a new link</a>
          <router-link to="/login" class="btn btn-secondary">Return to log in</router-link>
        </div>

        <div v-if="handoffError" class="form-error-summary" role="alert">{{ handoffError }}</div>
      </div>
    </div>
  </div>
</template>

<script>
/**
 * Where an emailed provider invitation lands — from a medication order or from
 * the Add User form.
 *
 * The page used to run two whole account-creation flows from an unauthenticated
 * browser. The pending-invite branch posted a chosen password to
 * `/sessionless/updatePassword` and activated the keyon account; the med-order
 * branch went further and created the account outright — `/sessionless/register`
 * followed by a `CreateSubAccount` multipart carrying the password and
 * `accntStatus=Active`, then linked the new provider to the inviting patient's
 * care team. All of it is dead (`/sessionless/` is intercepted by the bridge and
 * mapped nowhere) and none of it should return: a public endpoint that creates an
 * active account with a credential, in an organization named by a query
 * parameter, is the exact defect the port set out to remove. Tenancy comes from
 * the access token now, and `POST /users` refuses `accountnumber` and `password`
 * outright rather than ignoring them.
 *
 * The replacement is an administrator creating the provider with `POST /users`
 * or — for a prescriber named on a medication order — the patient or a holder
 * of privilege 5 inviting them with `POST /patients/{patient}/provider-invitations`
 * (decision 15), which creates the provider signed-in-side and links them to
 * that chart once they accept; either way pacmny-auth then invites them to
 * choose their own password. That leaves
 * this page one job: get an invitee to the sign-in service, where
 * `must_change_password` takes them through it. The button starts the SPA's
 * ordinary OIDC login rather than linking to a page on the provider, so no
 * pacmny-auth URL is hard-coded here.
 */
import store from '@/store';
import oidc from '@/auth/oidcClient';
import { FORGOT_PASSWORD_URL } from '@/api/identityProvider';
import PublicBrandHeader from '@/components/public-layouts/PublicBrandHeader.vue';
import { INVITE_EXPIRED } from '@/utils/pendingAccountStatus';

export default {
  name: 'verify-provider-page',
  components: {
    PublicBrandHeader,
  },
  props: {
    accountType: { type: String, default: 'Provider' },
    unique: { type: String, default: '' },
    accntnum: { type: String, default: '' },
    firstName: { type: String, default: '' },
    lastName: { type: String, default: '' },
    npi: { type: String, default: '' },
    email: { type: String, default: '' },
    patientUniqueId: { type: String, default: '' },
    // The keyon-era invite e-mail's single-use token (router query `invite`),
    // which authorized that page's public writes. Nothing here writes, and the
    // proof of an invitation is now pacmny-auth's own link, so it is accepted
    // and ignored. Left undeclared, it would fall through onto the root element
    // as an attribute and put the token in the DOM.
    invite: { type: String, default: '' },
  },
  data() {
    return {
      // A token-free page on the provider: it answers the same way for a known
      // and an unknown address, so linking to it says nothing about who has an
      // account here. The invitation link itself carries a token and can only be
      // minted and mailed by pacmny-auth. It is offered for an invitation that
      // cannot be found, not for an expired one: only the organization sending it
      // again revives an expired invitation (utils/pendingAccountStatus.js).
      forgotPasswordUrl: FORGOT_PASSWORD_URL,
      // Main's sentence, word for word (see VerifyAccountPage).
      inviteExpired: INVITE_EXPIRED,
      redirecting: false,
      handoffError: '',
    };
  },
  computed: {
    isLoggedIn() {
      return store.getters['login/isLoggedIn'];
    },
    accountTypeLabel() {
      return this.accountType || 'Provider';
    },
    /**
     * The two invitation kinds still read differently, because what the invitee
     * has to do next differs: one already has an account waiting, the other does
     * not have one at all and needs their administrator to create it.
     */
    inviteNotice() {
      return this.unique
        ? 'You have been invited to join this organization\'s EMR as a provider, and an account is waiting for you.'
        : 'You have been invited to join this organization\'s EMR as a provider.';
    },
  },
  methods: {
    async continueToSignIn() {
      this.redirecting = true;
      this.handoffError = '';
      try {
        await oidc.beginLogin({ returnTo: '/welcome' });
      } catch (err) {
        // A full-page redirect that never happens leaves the button spinning
        // forever, so an unreachable provider has to be said out loud.
        this.redirecting = false;
        this.handoffError = 'The sign-in service could not be reached. Please try again in a moment.';
        console.error('Could not start sign-in:', err);
      }
    },
  },
};
</script>

<style scoped lang="scss">
@import "../../stylesheets/mixins/alignment";

.content {
  width: 100%;
  height: auto;
  background: url("../../assets/images/bg.jpg") no-repeat center fixed;
  background-size: cover;
  position: relative;
}

.reset-wrapper {
  height: 100vh;
  width: 100%;
  position: relative;
}

.reset {
  background-color: #fff;
  padding: 2rem;
  box-shadow: 0 2px 2px 0 rgba(0, 0, 0, 0.14), 0 3px 1px -2px rgba(0, 0, 0, 0.12),
    0 1px 5px 0 rgba(0, 0, 0, 0.2);
  @include absoluteCenter(860px);

  > h3 {
    text-align: center;
  }
}

.invite-notice {
  margin-bottom: 1rem;
  color: #444;
}

.accountform-btn {
  display: flex;
  gap: 10px;
  margin-top: 16px;
}

.form-error-summary {
  margin: 0.75rem 0 0;
  padding: 0.9rem 1.15rem;
  background: #fdecea;
  border: 1px solid #f3b4b0;
  border-radius: 6px;
  color: #9b2c2c;
}

@media only screen and (max-width: 768px) {
  .reset {
    width: 95%;
  }
}
</style>
